import React, { useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import {
    findChartContainerId,
    parseChartDataFromSource,
    prepareAiHtmlForRender,
} from "./ai-html-utils";

const G2PLOT_CDN =
    "https://unpkg.com/@antv/g2plot@2.4.31/dist/g2plot.min.js";

let g2PlotLoadPromise = null;

// Allowlist for sanitizing AI/MCP-generated HTML before it is rendered via
// dangerouslySetInnerHTML. Only tags/attributes needed for tables and chart
// containers are permitted; scripts, iframes, event handlers, and
// javascript:/data: URLs on non-image elements are stripped by DOMPurify.
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        "div", "span", "p", "br", "hr",
        "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
        "b", "strong", "i", "em", "u", "small", "mark", "sup", "sub",
        "ul", "ol", "li", "a",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "pre", "code", "blockquote",
        "img",
    ],
    ALLOWED_ATTR: [
        "id", "class", "style",
        "href", "target", "rel",
        "colspan", "rowspan", "align", "valign", "width", "height",
        "border", "cellpadding", "cellspacing",
        "src", "alt", "title",
    ],
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "svg"],
    FORBID_ATTR: [/^on\S+/i],
};

// AI replies can embed generated chart images (e.g. <img src="https://...">).
// Only allow https or inline raster image sources on <img>.
const SAFE_IMG_SRC = /^(?:https:\/\/|data:image\/(?:png|jpe?g|gif|webp);base64,)/i;

DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (node.nodeName === "IMG" && data.attrName === "src") {
        if (!SAFE_IMG_SRC.test((data.attrValue || "").trim())) {
            data.keepAttr = false;
        }
    }
});

function loadG2Plot() {
    if (typeof window !== "undefined" && window.G2Plot) {
        return Promise.resolve(window.G2Plot);
    }
    if (!g2PlotLoadPromise) {
        g2PlotLoadPromise = new Promise((resolve, reject) => {
            const existing = document.querySelector('script[data-mcp-g2plot="1"]');
            if (existing) {
                existing.addEventListener("load", () => resolve(window.G2Plot));
                existing.addEventListener("error", reject);
                return;
            }
            const script = document.createElement("script");
            script.src = G2PLOT_CDN;
            script.async = true;
            script.dataset.mcpG2plot = "1";
            script.onload = () => resolve(window.G2Plot);
            script.onerror = () => {
                g2PlotLoadPromise = null;
                reject(new Error("Failed to load G2Plot"));
            };
            document.head.appendChild(script);
        });
    }
    return g2PlotLoadPromise;
}

function chartContainerHasPlot(el) {
    return el && (el.querySelector("canvas") || el.querySelector("svg"));
}

async function renderPieFallback(containerId, data) {
    const G2 = await loadG2Plot();
    const el = document.getElementById(containerId);
    if (!el || !Array.isArray(data) || !data.length) return;
    el.innerHTML = "";
    const Pie = G2.Pie || (G2.default && G2.default.Pie);
    if (!Pie) return;
    const pie = new Pie(containerId, {
        data,
        angleField: "value",
        colorField: "category",
        radius: 1,
        innerRadius: 0.55,
        legend: { position: "right" },
        label: { type: "outer", content: "{name}: {percentage}" },
        tooltip: { fields: ["category", "value"] },
        interactions: [{ type: "element-active" }],
    });
    pie.render();
}

function runChartScripts(scripts) {
    scripts.forEach((code) => {
        try {
            // eslint-disable-next-line no-new-func
            const run = new Function("document", "window", code);
            run(document, window);
        } catch (err) {
            console.error("MCP chart script error:", err);
        }
    });
}

/**
 * Renders AI HTML (tables, chart containers). Chart JS is never shown as text.
 */
export default function McpHtmlMessage({ html, isStreaming }) {
    const containerRef = useRef(null);
    const runIdRef = useRef(0);

    const { markup, scripts, containerId, chartData } = useMemo(() => {
        const prepared = prepareAiHtmlForRender(html);
        let { markup: mk, scripts: sc } = prepared;
        const id = findChartContainerId(mk, sc);
        const data = parseChartDataFromSource(html, sc);

        if (id && !mk.includes(`id="${id}"`) && !mk.includes(`id='${id}'`)) {
            mk = `${mk}<div id="${id}" style="min-height:280px;width:100%"></div>`;
        }

        return {
            markup: mk,
            scripts: sc,
            containerId: id,
            chartData: data,
        };
    }, [html]);

    const sanitizedMarkup = useMemo(
        () => DOMPurify.sanitize(markup, SANITIZE_CONFIG),
        [markup]
    );

    useEffect(() => {
        if (isStreaming) return undefined;

        const needsChart = scripts.length > 0 || (containerId && chartData);
        if (!needsChart) return undefined;

        const runId = ++runIdRef.current;
        let cancelled = false;

        const run = async () => {
            try {
                await loadG2Plot();
            } catch {
                // fallback may still fail; container stays empty
            }
            if (cancelled || runId !== runIdRef.current) return;

            if (scripts.length) {
                runChartScripts(scripts);
            }

            if (cancelled || runId !== runIdRef.current) return;

            if (containerId && chartData) {
                const el = document.getElementById(containerId);
                if (!chartContainerHasPlot(el)) {
                    try {
                        await renderPieFallback(containerId, chartData);
                    } catch (err) {
                        console.error("MCP pie chart fallback error:", err);
                    }
                }
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [markup, scripts, containerId, chartData, isStreaming]);

    if (!markup && !isStreaming) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="mcp-message-html mcp-message-html--rich"
            dangerouslySetInnerHTML={{ __html: sanitizedMarkup }}
            style={{ width: "100%", overflow: "auto" }}
        />
    );
}
